import { useContext, useEffect, useState, useMemo, createContext } from "react";
import { useNavigate, useParams } from "react-router";
import {
	getCardFieldsArray,
	memoFields as memoValues,
	docFields as docValues,
	getSelectedDaysIds,
	isCurrentOrFutureWeek,
	checkHasUpcomingDays,
} from "../helpers";
import { useAssignSubstitutor, useDates, useUserDetails, useUserNotes } from "Api/Coordinator/Gebruikers/PlanscreenView/Context";

const ControlShermContext = createContext();

export function useControlshermContext() {
	const context = useContext(ControlShermContext);

	if (!context) throw new Error("Use controlsherm context within provider.");

	return context;
}

export const ControlshermProvider = ({ children }) => {
	const navigate = useNavigate();
	const params = useParams();
	const { contractId, dayId, gebruikerId, subsId } = params;

	const [details, setDetails] = useState({});
	const [memoFields, setMemoFields] = useState([]);
	const [notesFields, setNotesFields] = useState([]);
	const [isOpen, setIsOpen] = useState(false);
	const [dates, setDates] = useState([]);
	const [notes, setNotes] = useState([]);
	const [isButtonsEnabled, setIsButtonsEnabled] = useState(true);
	const [selectedDates, setSelectedDates] = useState([]);
	const [hasUpcomingDays, setHasUpcomingDays] = useState(false);
	const [isButtonDisabled, setIsButtonDisabled] = useState(false);
	const [errorDetails, setErrorDetails] = useState(null);

	const [isDatesLoading, setIsDatesLoading] = useState(false);
	const [errorDates, setErrorDates] = useState(null);

	const [errorNotes, setErrorNotes] = useState(null);
	const [checkboxes, setCheckboxes] = useState({
		melderEmail: false,
		melderSms: false,
		vervangerEmail: false,
		vervangerSms: false
	});


	const { trigger: assignApi, isMutating } = useAssignSubstitutor();

	const {
		data: notesResponse,
		isLoading: isNotesLoading,
		mutate: refetchNotes,
	} = useUserNotes(
		gebruikerId,
		subsId,
		contractId,
		selectedDates
	);

	useEffect(() => {
		if (notesResponse?.data) {
			setNotes(notesResponse.data);
			setErrorNotes(null);
		}
	}, [notesResponse]);

	const {
		data: userDetailsResponse,
		error,
		isLoading: isDetailsLoading,
	} = useUserDetails(
		gebruikerId,
		subsId,
		dayId,
		contractId
	);

	useEffect(() => {
		if (!userDetailsResponse) return;

		if (
			userDetailsResponse.error === "invalid_link"
		) {
			navigate("/no-access");
			return;
		}

		if (userDetailsResponse?.data) {
			setDetails(userDetailsResponse.data)

		}
	}, [userDetailsResponse, navigate]);

	useEffect(() => {
		setMemoFields(getCardFieldsArray(memoValues, details));
	}, [details]);

	useEffect(() => {
		setNotesFields(getCardFieldsArray(docValues, notes, false));
	}, [notes]);

	useEffect(() => {
		if (selectedDates.length === 0) {
			setIsButtonDisabled(true);
		} else {
			setIsButtonDisabled(false);
		}
	}, [selectedDates]);

	useEffect(() => {
		setIsButtonsEnabled(notesFields.length < 1 && selectedDates.length > 0);
	}, [notesFields, selectedDates]);



const {
    data: datesResponse,
    isLoading,
     mutate: refetchDates,
} = useDates(
    gebruikerId,
    subsId,
    dayId,
    contractId
);


	useEffect(() => {
		if (datesResponse?.data) {
			setDates(datesResponse.data);
		}
	}, [datesResponse]);


	// const fetchNotes = async (selectedDatesToLoad = selectedDates) => {
	// 	if (!selectedDatesToLoad?.length) return;

	// 	try {
	// 		setErrorNotes(null);
	// 		const res = await getNotes({
	// 			data: {
	// 				gebruikerId,
	// 				subsId,
	// 				contractId,
	// 				selectedDates: selectedDatesToLoad,
	// 			},
	// 		});
	// 		setNotes(res.data || []);
	// 	} catch (err) {
	// 		setErrorNotes("Failed to load notes");
	// 	}
	// };


	useEffect(() => {
		setSelectedDates(getSelectedDaysIds(dates));
	}, [dates]);

	const handleAssignment = async (status) => {
		const substEmail = [];
		const substSms = [];

		if (checkboxes.melderEmail) {
			substEmail.push({ id: details.recipientsList.melder.id, userType: "melder" });
		}

		if (checkboxes.melderSms) {
			substSms.push({
				id: details.recipientsList.melder.id,
				userType: "melder",
				message: `Beste melder, Verzoek ${subsId} ${details.recipientsList.melder.name}`,
			});
		}

		if (checkboxes.vervangerEmail) {
			substEmail.push({ id: details.recipientsList.vervanger.id, userType: "vervanger" });
		}

		if (checkboxes.vervangerSms) {
			substSms.push({
				id: details.recipientsList.vervanger.id,
				userType: "vervanger",
				message: `Beste vervanger, Verzoek ${subsId} ${details.recipientsList.vervanger.name}`,
			});
		}

		try {
			const res = await assignApi({
				url: `controlscreen/assign/${gebruikerId}/${subsId}`,
				method: "patch",
				data: {
					substitutionDayIds: selectedDates,
					status,
					substEmail,
					substSms,
				},
			});

			if (res?.status) {
				navigate(`/verzoeken/overzicht/${subsId}`);
			} else {
				console.error("Substitutor was not assigned.", res);
			}
		} catch (error) {
			console.error("Assign substitutor failed:", error);
		}
	};

	const contextValues = useMemo(
		() => ({
			contractId,
			dayId,
			gebruikerId,
			subsId,
			details,
			setDetails,
			memoFields,
			notesFields,
			isOpen,
			setIsOpen,
			dates,
			setDates,
			notes,
			setNotes,
			isButtonsEnabled,
			setIsButtonDisabled,
			setIsButtonsEnabled,
			isButtonDisabled,
			isDetailsLoading,
			errorDates,
			selectedDates,
			setSelectedDates,
			refetchNotes,
			errorDetails,
			isDatesLoading,
			errorDates,
			handleAssignment,
			 refetchDates,
			errorNotes,
			hasUpcomingDays,
			checkboxes,
			setCheckboxes,
		}),
		[
			contractId,
			dayId,
			gebruikerId,
			subsId,
			details,
			memoFields,
			notesFields,
			 refetchDates,
			isOpen,
			dates,
			notes,
			isButtonsEnabled,
			isButtonDisabled,
			isDetailsLoading,
			errorDates,
			selectedDates,
			errorDetails,
			isDatesLoading,
			errorDates,
			handleAssignment,
			errorNotes,
			hasUpcomingDays,
			checkboxes,
		]
	);
	return (
		<ControlShermContext.Provider value={contextValues}>
			{children}
		</ControlShermContext.Provider>
	);
};
